import React, { useRef, useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Conteneur scroll horizontal avec boutons flèches gauche/droite qui suivent
 * verticalement le viewport (utile sur les longues tables : les flèches restent
 * accessibles sans devoir descendre tout en bas de la page).
 *
 * - Boutons masqués si le contenu ne déborde pas.
 * - Boutons masqués aux extrémités correspondantes.
 * - Position verticale recalculée à chaque scroll/resize : on aligne les flèches
 *   sur le centre de la portion VISIBLE de la table dans le viewport.
 */
export default function HorizontalScrollContainer({ children, className = "", testId, scrollAmount = 320 }) {
  const wrapperRef = useRef(null);
  const scrollRef = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);
  // Position Y des flèches (en pixels) relative au conteneur wrapper.
  // `null` => table hors viewport, on masque tout.
  const [arrowTop, setArrowTop] = useState(null);

  const updateOverflow = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setCanLeft(el.scrollLeft > 4);
    setCanRight(el.scrollLeft < max - 4);
  }, []);

  const updateArrowPosition = useCallback(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const rect = wrapper.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    // Portion visible de la table dans le viewport
    const visibleTop = Math.max(0, rect.top);
    const visibleBottom = Math.min(vh, rect.bottom);
    if (visibleBottom <= visibleTop + 40) {
      // Table hors écran (ou marge insuffisante) : masquer les flèches
      setArrowTop(null);
      return;
    }
    const visibleCenter = (visibleTop + visibleBottom) / 2;
    // Position relative au top du wrapper
    setArrowTop(visibleCenter - rect.top);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateOverflow();
    updateArrowPosition();
    el.addEventListener("scroll", updateOverflow, { passive: true });
    const ro = new ResizeObserver(() => {
      updateOverflow();
      updateArrowPosition();
    });
    ro.observe(el);
    if (wrapperRef.current) ro.observe(wrapperRef.current);
    window.addEventListener("resize", updateArrowPosition);
    // On écoute le scroll sur tous les ancêtres scrollables (le layout utilise
    // un <main overflow-y-auto> donc `window` ne reçoit aucun scroll).
    const scrollableAncestors = [];
    let node = wrapperRef.current?.parentElement;
    while (node && node !== document.body) {
      const cs = window.getComputedStyle(node);
      if (/(auto|scroll|overlay)/.test(cs.overflowY) || /(auto|scroll|overlay)/.test(cs.overflow)) {
        node.addEventListener("scroll", updateArrowPosition, { passive: true });
        scrollableAncestors.push(node);
      }
      node = node.parentElement;
    }
    window.addEventListener("scroll", updateArrowPosition, { passive: true });
    return () => {
      el.removeEventListener("scroll", updateOverflow);
      ro.disconnect();
      window.removeEventListener("resize", updateArrowPosition);
      window.removeEventListener("scroll", updateArrowPosition);
      scrollableAncestors.forEach((n) => n.removeEventListener("scroll", updateArrowPosition));
    };
  }, [updateOverflow, updateArrowPosition, children]);

  const scrollBy = (delta) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: delta, behavior: "smooth" });
  };

  const showArrows = arrowTop !== null;

  return (
    <div ref={wrapperRef} className="relative" data-testid={testId}>
      <div ref={scrollRef} className={`overflow-x-auto ${className}`}>
        {children}
      </div>
      {showArrows && canLeft && (
        <button
          type="button"
          onClick={() => scrollBy(-scrollAmount)}
          aria-label="Défiler vers la gauche"
          data-testid={testId ? `${testId}-scroll-left` : "scroll-left"}
          style={{ top: `${arrowTop}px` }}
          className="absolute left-1 -translate-y-1/2 z-10 h-9 w-9 flex items-center justify-center rounded-full bg-sky-50 border border-sky-200 shadow-md hover:bg-sky-100 hover:border-sky-300 transition-colors"
        >
          <ChevronLeft size={18} className="text-sky-500" />
        </button>
      )}
      {showArrows && canRight && (
        <button
          type="button"
          onClick={() => scrollBy(scrollAmount)}
          aria-label="Défiler vers la droite"
          data-testid={testId ? `${testId}-scroll-right` : "scroll-right"}
          style={{ top: `${arrowTop}px` }}
          className="absolute right-1 -translate-y-1/2 z-10 h-9 w-9 flex items-center justify-center rounded-full bg-sky-50 border border-sky-200 shadow-md hover:bg-sky-100 hover:border-sky-300 transition-colors"
        >
          <ChevronRight size={18} className="text-sky-500" />
        </button>
      )}
    </div>
  );
}
